import React, { useEffect, useState } from "react";
import "./LessonDetails.css";
import {
  BrowserIcon,
  ClockIcon,
  DeleteIcon,
  EditIcon,
  MoviePlayIcon,
  OutlinePlayCircleIcon,
  PdfIcon,
} from "../../../../icons/reactIcons";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../../../components/custom-breadcrumb/Breadcrumb";
import { useAppDispatch, useAppSelector } from "../../../../redux/store/hooks";
import {
  clearLessonDetails,
  deleteLessonThunk,
  fetchLessonByIdThunk,
  fetchLessonListThunk,
} from "../../../../redux/slices/lessonSlice";
import Loader from "../../../../components/custom-loader/Loader";
import ConfirmUserActionModal from "../../../../components/confirm-modal/ConfirmUserActionModal";

const LessonDetails = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { lessonDetails, loading, error } = useAppSelector(
    (state) => state.lesson,
  );

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (lessonId) {
      dispatch(fetchLessonByIdThunk(lessonId));
    }

    return () => {
      dispatch(clearLessonDetails());
    };
  }, [dispatch, lessonId]);

  const handleDeleteLesson = async () => {
    if (!lessonId) return;

    try {
      setSubmitting(true);

      const result = await dispatch(deleteLessonThunk(lessonId));

      if (deleteLessonThunk.fulfilled.match(result)) {
        dispatch(fetchLessonListThunk());

        navigate("/lessons");
      }
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setSubmitting(false);
      setOpenDeleteModal(false);
    }
  };

  const breadcrumbItems = [
    { label: "Lessons", onClick: () => navigate("/lessons") },
    { label: "Lesson Details", disabled: true },
  ];

  if (loading) {
    return (
      <div className="lesson-details-container">
        <Breadcrumb items={breadcrumbItems} />
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="lesson-details-container">
        <Breadcrumb items={breadcrumbItems} />
        <div style={{ padding: "20px", color: "red" }}>{error}</div>
      </div>
    );
  }

  if (!lessonDetails) return null;

  const {
    name,
    description,
    lessonType,
    lessonTypeDetails,
    otherAttachments,
    lessonLongDescription,
    categoriesMapped,
  } = lessonDetails;

  const mainUrl = lessonTypeDetails?.[0]?.lessonUrl;
  const contentButtonLabel =
    lessonType === "document"
      ? "Open Pdf"
      : lessonType === "video"
        ? "Open Video"
        : "Open Website";
  const ContentIcon =
    lessonType === "document"
      ? PdfIcon
      : lessonType === "video"
        ? OutlinePlayCircleIcon
        : BrowserIcon;

  return (
    <div className="lesson-details-container">
      <Breadcrumb items={breadcrumbItems} />

      <div className="lesson-header">
        <h2 className="page-title">Lesson Details</h2>
        <div className="lesson-actions">
          <button
            className="edit-btn"
            onClick={() => navigate(`/lessons/edit/${lessonId}`)}
          >
            <EditIcon /> Edit
          </button>
          <button
            className="delete-btn"
            onClick={() => setOpenDeleteModal(true)}
          >
            <DeleteIcon /> Delete
          </button>
        </div>
      </div>

      <div className="lesson-card">
        <div className="lesson-title-section">
          <h3>{name}</h3>

          {lessonTypeDetails?.[0]?.lessonDuration && (
            <div className="lesson-duration">
              <ClockIcon fontSize={14} />
              <span>{lessonTypeDetails[0].lessonDuration}</span>
            </div>
          )}
        </div>

        <p className="lesson-description">{description}</p>

        {categoriesMapped?.length > 0 && (
          <div className="lesson-tags">
            {categoriesMapped.map((cat) => (
              <span key={cat.id} className="tag">
                {cat.name}
              </span>
            ))}
          </div>
        )}

        {mainUrl && (
          <div className="lesson-content-open-area">
            <p className="lesson-content-note">
              Lesson content will open in a new browser tab. Click the button
              below to continue.
            </p>

            <div className="lesson-content-open-card">
              <ContentIcon className="lesson-content-open-icon" />
              <a
                href={mainUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="open-file-btn"
              >
                {contentButtonLabel}
              </a>
            </div>
          </div>
        )}

        {otherAttachments?.length > 0 && (
          <div className="attachments">
            <h4>Other Attachments</h4>

            {otherAttachments.map((attachment, index) => (
              <div className="attachment" key={index}>
                <span className="icon">
                  {attachment.attachmentType === "document" && (
                    <PdfIcon fontSize="18px" />
                  )}
                  {attachment.attachmentType === "website" && (
                    <BrowserIcon fontSize="18px" />
                  )}
                  {attachment.attachmentType === "video" && (
                    <MoviePlayIcon fontSize="18px" />
                  )}
                </span>

                <p>
                  {attachment.attachmentType === "document" && (
                    <>
                      Document -{" "}
                      <a
                        href={
                          attachment.attachmentUrl ||
                          attachment?.attachmentFileUrl
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="read-link"
                      >
                        Read now
                      </a>
                    </>
                  )}

                  {attachment.attachmentType === "website" && (
                    <>
                      Website -{" "}
                      <a
                        href={attachment.attachmentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="visit-link"
                      >
                        Visit now
                      </a>
                    </>
                  )}

                  {attachment.attachmentType === "video" && (
                    <>
                      Video -{" "}
                      <a
                        href={attachment.attachmentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="watch-link"
                      >
                        Watch now
                      </a>
                    </>
                  )}
                </p>

                {attachment.attachmentDuration && (
                  <span className="video-duration">
                    <ClockIcon fontSize={12} /> {attachment.attachmentDuration}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {lessonLongDescription && (
          <div className="key-points">
            <h4>Details</h4>
            <div
              dangerouslySetInnerHTML={{
                __html: lessonLongDescription,
              }}
            />
          </div>
        )}
      </div>

      {openDeleteModal && (
        <ConfirmUserActionModal
          type="delete"
          userName={lessonDetails?.name}
          loading={submitting}
          onClose={() => setOpenDeleteModal(false)}
          onConfirm={handleDeleteLesson}
        />
      )}
    </div>
  );
};

export default LessonDetails;
